<template>
  <Flex
    direction="column"
    :gap="1"
  >
    <TasksCard
      :image="'/img/online/website-service.svg'"
      :heading="t('website_service.onboarding.title')"
      :description="t('website_service.onboarding.description')"
    >
      <template #extraContent>
        <DemoButton />
      </template>
      <template #tasks>
        <BaseTask
          v-for="task in tasks"
          :key="task.heading"
          :heading="task.heading"
          :state="task.state"
          :buttonIcon="task.buttonIcon"
          @buttonClick="task.buttonClick"
          v-test="task.vTest"
        />
      </template>
    </TasksCard>

    <GiftcardWidgetSettings v-if="inReview" />

    <transition
      name="fade-slide-2"
      appear
    >
      <BaseCard>
        <Flex
          direction="column"
          :gap="1"
        >
          <ProcessSteps :newTerms="!isLegacyTerms" />
          <HelpPageButton />
        </Flex>
      </BaseCard>
    </transition>

    <TermsModal
      v-if="showTermsModal"
      :viewOnly="acceptedTerms"
      @close="showTermsModal = false"
    />
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import { computed, inject, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { IconName } from '@/components/base-icon/types';
import TasksCard from '@/modules/online/TasksCard.vue';
import { type Website, WebsiteStatusEnum } from '@/types';
import { Airtable } from '../Airtable';
import DemoButton from '../DemoButton.vue';
import GiftcardWidgetSettings from '../dashboard/GiftcardWidgetSettings.vue';
import HelpPageButton from '../HelpPageButton.vue';
import ProcessSteps from './ProcessSteps.vue';
import TermsModal from './TermsModal.vue';

type Task = {
  heading: string;
  state: 'completed' | 'required' | 'blocked' | 'default' | 'pending';
  buttonIcon?: IconName;
  buttonClick?: () => void;
  vTest?: string;
};

const website = inject<Ref<Website>>('website');

const { t } = useI18n();

const acceptedTerms = computed(() => !!website?.value?.termsAcceptedAt);
const inProduction = computed(
  () => website?.value?.status === WebsiteStatusEnum.InProduction,
);

const inReview = computed(
  () => website?.value?.status === WebsiteStatusEnum.Review,
);

const isLegacyTerms = computed(() => website?.value?.termsVersion === 1);
const customizedWebsite = computed(() => inProduction.value || inReview.value);
const showTermsModal = ref(false);

const onCustomizeWebsiteClick = () => {
  const { customizeWebsiteUrl } = Airtable();
  window.open(customizeWebsiteUrl, '_blank');
};

const onReviewClick = () => {
  window.open(
    'https://demodesk.com/book/treatwell-group/salonized-website-onboarding',
    '_blank',
  );
};

const tasks = computed<Task[]>(() => [
  {
    heading: t('website_service.onboarding.tasks.request_received'),
    state: 'completed',
    vTest: 'website-requested-task',
  },
  {
    heading: t('website_service.onboarding.tasks.accept_terms'),
    state: acceptedTerms.value ? 'completed' : 'required',
    buttonIcon: acceptedTerms.value ? 'eye' : 'edit',
    buttonClick: () => (showTermsModal.value = true),
    vTest: 'website-accept-terms-task',
  },
  {
    heading: t('website_service.onboarding.tasks.customize_website'),
    state: customizedWebsite.value
      ? 'completed'
      : acceptedTerms.value
        ? 'required'
        : 'blocked',
    buttonIcon:
      customizedWebsite.value || !acceptedTerms.value ? undefined : 'edit',
    buttonClick: () => onCustomizeWebsiteClick(),
    vTest: 'website-customize-website-task',
  },
  {
    heading: t('website_service.onboarding.tasks.in_production'),
    state: inReview.value
      ? 'completed'
      : inProduction.value
        ? 'pending'
        : 'blocked',
    vTest: 'website-in-production-task',
  },
  {
    heading: t('website_service.onboarding.tasks.review'),
    state: inReview.value ? 'required' : 'blocked',
    buttonIcon: inReview.value ? 'external_link' : undefined,
    buttonClick: () => onReviewClick(),
    vTest: 'website-review-task',
  },
  {
    heading: t('website_service.onboarding.tasks.website_live'),
    state: 'blocked',
    vTest: 'website-live-task',
  },
]);
</script>
